import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as blogApi from '../api/blogApi';
import { ConfirmDialog, EmptyState, ErrorBanner, TableSkeleton } from '../components/ui';
import { IconPencil, IconPlus, IconTrash } from '../components/icons';
import { BlogPostSummary, formatPublishedDate } from '../types/blog';

/** Green when live, grey while it is still being written. */
function StatusPill({ status }: { status: BlogPostSummary['status'] }) {
  const published = status === 'published';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
        published ? 'bg-ok/15 text-ok' : 'bg-edge text-sub'
      }`}
    >
      {published ? 'Published' : 'Draft'}
    </span>
  );
}

export default function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [confirmDelete, setConfirmDelete] = useState<BlogPostSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPosts(await blogApi.getPosts());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await blogApi.deletePost(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">The Scent Journal</h1>
          <p className="mt-1 text-sm text-sub">Manage blog posts</p>
        </div>
        <Link to="/blog/new" className="btn-gold">
          <IconPlus className="h-4 w-4" />
          New Post
        </Link>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      <div className="card overflow-hidden">
        {loading ? (
          <TableSkeleton rows={5} />
        ) : posts.length === 0 ? (
          <EmptyState
            message="No posts yet. Write your first post."
            hint="Posts appear on theperfumeculture.com/blog once published."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-edge">
                <tr>
                  <th className="th">Title</th>
                  <th className="th">Status</th>
                  <th className="th">Published Date</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {posts.map((post) => (
                  <tr key={post.id} className="transition-colors hover:bg-edge/20">
                    {/* The one cell allowed to wrap — a 120-character title on
                        one line would push Actions off the side of the table. */}
                    <td className="px-4 py-3 text-sm">
                      <Link
                        to={`/blog/${post.id}/edit`}
                        className="font-medium text-ink hover:text-gold"
                      >
                        {post.title}
                      </Link>
                      <p className="mt-0.5 font-mono text-xs text-sub">/blog/{post.slug}</p>
                    </td>
                    <td className="td">
                      <StatusPill status={post.status} />
                    </td>
                    <td className="td text-sub">{formatPublishedDate(post.published_at)}</td>
                    <td className="td text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => navigate(`/blog/${post.id}/edit`)}
                          className="btn-ghost !px-2"
                          title="Edit post"
                        >
                          <IconPencil className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </button>
                        <button
                          onClick={() => setConfirmDelete(post)}
                          className="btn-ghost !px-2 hover:text-bad"
                          title="Delete post"
                        >
                          <IconTrash className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* The empty state's own call to action, below the card so it reads as the
          next step rather than as part of the (absent) table. */}
      {!loading && posts.length === 0 && !error && (
        <div className="flex justify-center">
          <Link to="/blog/new" className="btn-gold">
            <IconPlus className="h-4 w-4" />
            Write First Post
          </Link>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete post"
        message={
          <>
            Delete this post? This cannot be undone.
            {confirmDelete && (
              <span className="mt-2 block font-medium text-ink">{confirmDelete.title}</span>
            )}
          </>
        }
        confirmLabel="Delete"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}
